import React, {
	BaseSyntheticEvent,
	ChangeEvent,
	useEffect,
	useRef,
	useState,
} from 'react';

import ColorPicker from 'components/basic/color-picker/ColorPicker';
import { DatasetDropdown } from 'components/basic/dropdowns/dataset/DatasetDropdown';
import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { Button } from 'primereact/button';
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { DataTable } from 'primereact/datatable';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { FileUpload, FileUploadUploadParams } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'react-tooltip';
import { useScadaStore } from 'store/scada-store';
import { ChronoUnitLegacy } from 'utils/time-utils';
import { ImagesUploadResponse, LabelLayers, ScadaLabel, ScadaProps } from '../scadaTypes';
import { IMAGE_UPLOAD_URL, getScadaImageUrl, tempId } from '../utils/scadaUtils';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import { ToggleButton, ToggleButtonChangeParams } from 'primereact/togglebutton';
import { isObjectEmpty } from 'utils/utils';
import { DEFAULT_LAYER, defaultLabel, defaultLayer, defaultScadaConfig } from '../utils/default-values';

type ScadaEditorProps = {
	config: ScadaProps;
	onConfigChange: (config: ScadaProps) => void;
};

const ScadaEditor: React.FC<ScadaEditorProps> = ({
	config,
	onConfigChange,
}) => {
	const [currentConfig, setCurrentConfig] = useState(defaultScadaConfig);
	const [newLayer, setNewLayer] = useState<LabelLayers>(defaultLayer);
	const toast = useRef<Toast>(null);
	const downloadRef = useRef<HTMLAnchorElement>(null);
	const addLabelRef = useRef<HTMLDivElement>(null);
	const scadaStore = useScadaStore();

	const scadaId = config.id ?? tempId(config);
	const scada = scadaStore.scadaMap.get(scadaId);
	const scadaLabels = scada?.labels;
	const scadaLabelLayers = scada?.labelLayers;

	useEffect(() => {
		if (isObjectEmpty(config)) onConfigChange(currentConfig);
		if (!scada) {
			onConfigChange({ ...config, id: currentConfig.id});
			scadaStore.addScada({
				scadaId: currentConfig.id ?? scadaId,
				labels: config.elements ?? [],
				labelLayers: config.labelLayers ?? [],
			});
		} else {
			setCurrentConfig({
				...config,
				elements: scadaLabels,
				labelLayers: scadaLabelLayers,
				id: config.id ?? currentConfig.id,
			});
			onConfigChange({
				...config,
				elements: scadaLabels,
				labelLayers: scadaLabelLayers,
				id: config.id ?? currentConfig.id,
			});
		}
	}, []);

	useEffect(() => {
		addLabelRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [config.elements?.length])
	
	const elements = [...(currentConfig.elements ?? [])];
	const scadaBgUrl = getScadaImageUrl(config.scadaBackgroundId);
	const labelLayers = [...(currentConfig.labelLayers ?? [])]

	const handleConfigChange = (
		e:
			| ChangeEvent<HTMLInputElement>
			| BaseSyntheticEvent
			| DropdownChangeParams
			| CheckboxChangeParams,
	) => {
		const value =
			e.target.type === 'checkbox' ? e.target.checked : e.target.value;

		const newConfig = {
			...currentConfig,
			[e.target.name]: value,
		};

		setCurrentConfig(newConfig);
		onConfigChange(newConfig);
	};

	const updateElementsOnStates = (newElements: ScadaLabel[]) => {
		const updConfig = {
			...currentConfig,
			elements: newElements,
		};
		scadaStore.updateScadaLabels(currentConfig.id!, newElements);
		setCurrentConfig(updConfig);
		onConfigChange(updConfig);
	};

	const handleLabelLayerChange = (e: ToggleButtonChangeParams, layer: string) => {
		const updLayer = labelLayers.map(lg =>
			lg.layer === layer ? { ...lg, visible: e.value } : lg,
		);
		scadaStore.updateScadaLabelLayers(currentConfig.id!, updLayer);
		setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
		onConfigChange({ ...currentConfig, labelLayers: updLayer });
	}

	const addLabelLayer = () => {
		if (labelLayers.find(lg => lg.layer === newLayer.layer)) return;
		const updLayer = [...labelLayers, newLayer];
		scadaStore.updateScadaLabelLayers(currentConfig.id!, updLayer);
		setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
		onConfigChange({ ...currentConfig, labelLayers: updLayer });
		setNewLayer(defaultLayer);
	}

	const removeLabelLayer = (index: number) => {
		const removedLayer = labelLayers.splice(index, 1);
		const updatedElements = elements.map(el =>
			el.layer === removedLayer[0].layer ? { ...el, layer: DEFAULT_LAYER } : el,
		);
		scadaStore.updateScadaLabels(currentConfig.id!, updatedElements)
		scadaStore.updateScadaLabelLayers(currentConfig.id!, labelLayers);
		setCurrentConfig({ ...currentConfig, labelLayers: labelLayers, elements: updatedElements });
		onConfigChange({ ...currentConfig, labelLayers: labelLayers, elements: updatedElements });
	}

	const handleDatasetChange = (
		value: string | number,
		option: ColumnBodyOptions,
	) => {
		const dataset = elements[option.rowIndex];
		const updDataset = {
			...dataset,
			[option.field]: value,
		};
		elements.splice(option.rowIndex, 1, updDataset);

		updateElementsOnStates(elements);
	};

	const confirmOnDelete = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		index: number,
	) => {
		e.preventDefault();
		confirmPopup({
			target: e.currentTarget,
			message: 'Are you sure you want to delete this label?',
			icon: 'pi pi-exclamation-triangle',
			acceptClassName: 'p-button-danger',
			accept: () => deleteColumn(index),
		});
	};

	const deleteColumn = (index: number) => {
		elements.splice(index, 1);
		updateElementsOnStates(elements);
	};

	const addLabel = (identifier: string | null, label: string | null) => {
		if (!identifier || !label) return;
		if (isIdentifierExists(identifier, label)) return;
		const newElements: ScadaLabel[] = [
			...elements,
			{
				...defaultLabel,
				identifier,
			},
		];

		updateElementsOnStates(newElements);

		toast.current?.show({
			severity: 'success',
			summary: 'Success',
			detail: `Label "${label}" was added`,
		});
	};

	const updateLabel = (
		identifier: string | null,
		label: string | null,
		index: number,
	) => {
		if (!identifier || !label) return;
		if (isIdentifierExists(identifier, label)) return;
		const updatedElements = elements.map((e, i) => i === index ? {...e,identifier}: e);

		updateElementsOnStates(updatedElements ?? []);
		
		toast.current?.show({
			severity: 'success',
			summary: 'Updated',
			detail: `Label was updated to "${label}"`,
		});
	};

	const isIdentifierExists = (identifier:string, label: string) => {
		const identifiers = elements.map((e) => e.identifier);
		if (identifiers?.includes(identifier)) {
			toast.current?.show({
				severity: 'warn',
				summary: 'Warn - label exists',
				detail: `Label "${label}" already exists in the list`,
			});
			return true;
		}
		return false;
}

	const onUpload = (e: FileUploadUploadParams) => {
		const response: ImagesUploadResponse = JSON.parse(e.xhr.response)

		const newConfig = {
			...currentConfig,
			scadaBackgroundId: response.id,
		};
		onConfigChange(newConfig);
		setCurrentConfig(newConfig);
		toast.current?.show({ severity: 'info', summary: 'Success', detail: 'Image Uploaded' });
	};

	const handleDownload = () => {
		downloadRef.current?.click();
	};

	const mappedLabelLayers = [...labelLayers].map((lg, index) => (
		<div className='p-inputgroup w-8 mb-3' key={lg.layer}>
			<InputText value={lg.layer} disabled />
			<ToggleButton
				onLabel='visible'
				offLabel='hidden'
				className='p-inputgroup-addon'
				checked={lg.visible}
				onChange={e => handleLabelLayerChange(e, lg.layer)}
			/>
			<Button
				icon='pi pi-times'
				type='button'
				onClick={() => removeLabelLayer(index)}
				className='p-inputgroup-addon p-button-sm p-button-danger'
				disabled={lg.layer === DEFAULT_LAYER}
			/>
		</div>
	));

	return (
		<div className='grid p-fluid'>
			<ConfirmPopup />
			<Toast ref={toast} />
			<InputLabelWrapper labelName='Refresh rate in sec' col={4}>
				<InputNumber
					value={currentConfig.refreshRate}
					name='refreshRate'
					onChange={e => handleConfigChange(e.originalEvent)}
					useGrouping={false}
					max={3600}
					min={0}
				/>
			</InputLabelWrapper>
			<InputLabelWrapper labelName='Not older than' col={4}>
				<div className='p-inputgroup'>
					<InputNumber
						value={currentConfig.maxDelayTime}
						name='maxDelayTime'
						onChange={e => handleConfigChange(e.originalEvent)}
						style={{ blockSize: '37px' }}
						useGrouping={false}
						max={100}
						min={0}
					/>
					<Dropdown
						value={currentConfig.maxDelayTimeUnit}
						options={Object.keys(ChronoUnitLegacy)}
						name='maxDelayTimeUnit'
						onChange={handleConfigChange}
						className='p-inputgroup-addon'
						style={{ blockSize: '37px' }}
					/>
				</div>
			</InputLabelWrapper>
			<InputLabelWrapper labelName='Label truncate size' col={4}>
				<InputNumber
					value={currentConfig.truncateSize}
					name='truncateSize'
					onChange={e => handleConfigChange(e.originalEvent)}
					useGrouping={false}
					max={100}
					min={0}
				/>
			</InputLabelWrapper>
			<InputLabelWrapper
				labelName='Show scada settings header'
				col={12}
				flexDirection='row-reverse'
				className='justify-content-end align-items-center gap-3'>
				<Checkbox
					checked={currentConfig.showScadaSettings}
					name='showScadaSettings'
					onChange={handleConfigChange}
					className='inline-block'
				/>
			</InputLabelWrapper>

			<InputLabelWrapper labelName='' col={4}>
				<FileUpload
					name='file'
					mode='basic'
					url={IMAGE_UPLOAD_URL}
					auto
					chooseOptions={{
						label: 'Upload Scada background',
						icon: 'pi pi-upload',
					}}
					onUpload={onUpload}
					accept='image/*'
				/>
			</InputLabelWrapper>
			{scadaBgUrl && (
				<InputLabelWrapper
					labelName=''
					col={8}
					className='justify-content-end gap-5'
					flexDirection='row'>
					<Image
						src={scadaBgUrl}
						alt='Scada background image'
						height='35'
						style={{ objectFit: 'contain' }}
						preview
					/>
					<Button
						onClick={handleDownload}
						type='button'
						label='Download'
						icon='pi pi-download'
						className='p-button-warning w-12rem'
					/>
					<a
						ref={downloadRef}
						style={{ display: 'none' }}
						href={`${scadaBgUrl}?download=${Math.random()}`}
					/>
				</InputLabelWrapper>
			)}
			<Accordion className='w-full'>
				<AccordionTab header='Label layers'>
					{mappedLabelLayers}
					<div className='p-inputgroup w-8'>
						<InputText
							placeholder='Add new layer...'
							value={newLayer.layer}
							onChange={e =>
								setNewLayer(prev => ({
									...prev,
									layer: e.target.value.trimStart(),
								}))
							}
						/>
						<ToggleButton
							onLabel='visible'
							offLabel='hidden'
							className='p-inputgroup-addon'
							checked={newLayer.visible}
							onChange={e =>
								setNewLayer(prev => ({ ...prev, visible: e.value }))
							}
						/>
						<Button
							icon='pi pi-check'
							onClick={addLabelLayer}
							type='button'
							className='p-inputgroup-addon p-button-sm p-button-success'
						/>
					</div>
				</AccordionTab>
			</Accordion>
			<DataTable value={elements} size='small' className='w-full mt-3'>
				<Column
					field='identifier'
					header='Dataset'
					className='w-full gap-3'
					body={(data, option) => (
						<DatasetDropdown
							value={data.identifier}
							onChange={(id, label) => updateLabel(id, label, option.rowIndex)}
						/>
					)}
				/>
				<Column
					field='xCoord'
					header='Position X, %'
					className='text-center'
					body={(data, option) => (
						<InputNumber
							style={{ minWidth: '4rem' }}
							value={data.xCoord}
							onChange={e => handleDatasetChange(e.value!, option)}
							maxFractionDigits={1}
							max={100}
							min={0}
						/>
					)}
				/>
				<Column
					field='yCoord'
					header='Position Y, %'
					className='text-center'
					body={(data, option) => (
						<InputNumber
							style={{ minWidth: '4rem' }}
							value={data.yCoord}
							onChange={e => handleDatasetChange(e.value!, option)}
							maxFractionDigits={1}
							max={100}
							min={0}
						/>
					)}
				/>
				<Column
					field='fontSize'
					header='Font size'
					body={(data, option) => (
						<InputNumber
							className='justify-self-end'
							style={{ minWidth: '3rem' }}
							value={data.fontSize}
							onChange={e => handleDatasetChange(e.value!, option)}
							max={120}
							min={1}
						/>
					)}
				/>
				<Column
					field='layer'
					header='Layer'
					body={(data, option) => (
						<Dropdown
							value={data.layer}
							onChange={e => handleDatasetChange(e.value, option)}
							style={{
								width: 'max(8rem, 10rem)',
								height: '3rem',
								padding: '0',
							}}
							className='p-inputtext-sm'
							options={[...labelLayers.map(lg => lg.layer)]}
						/>
					)}
				/>
				<Column
					field='bgColor'
					header='Bg color'
					body={(data, option) => (
						<ColorPicker
							value={
								data.bgColor.startsWith('#') ? data.bgColor : `#${data.bgColor}`
							}
							onChange={e => handleDatasetChange(e.target.value, option)}
						/>
					)}
				/>
				<Column
					style={{ width: '0' }}
					body={(_, options) => (
						<>
							<Tooltip id='delete-btn' />
							<Button
								data-tooltip-id='delete-btn'
								data-tooltip-content='Delete'
								icon='pi pi-trash'
								className='p-button-sm p-button-danger'
								onClick={e => confirmOnDelete(e, options.rowIndex)}
							/>
						</>
					)}
				/>
			</DataTable>
			<div ref={addLabelRef} className='flex mt-3 w-full'>
				<DatasetDropdown onChange={addLabel} />
			</div>
		</div>
	);
};

export default ScadaEditor;
